import { hawkSearchTracking } from './HawkSearchTracking'
import { StoreInstances } from 'src/store/StoreInstancesContainer'
import { OrderResponse } from 'src/api/cart/interfaces'
import { UserAttributeKey } from 'src/store/user/interfaces'
import {
  HawkSearchEvent,
  HawkSearchEventContext,
} from 'src/config/analytics/core/types/events'
import { Vehicle } from 'src/store/models/Vehicles'
import { HAWK_SITE_TYPE } from 'common/src/constants/constants'

export enum Event_Types {
  Search = 1,
  AddToCart = 2,
  Sales = 3,
}

const clientGuid = import.meta.env.VITE_HAWKSEARCH_CLIENT_GUID as string

export function prepareEventData(
  eventType: string,
  context: HawkSearchEventContext
): HawkSearchEvent[] {
  let eventObject

  if (eventType === 'Search') {
    eventObject = {
      Qs: context.Qs,
      TypeId: Event_Types[eventType as keyof typeof Event_Types],
      QueryId: context.isNewSearch
        ? hawkSearchTracking.replaceQueryIdCookie()
        : hawkSearchTracking.fetchOrGenerateQueryIdViaCookie(),
      RemoteAddr: context.RemoteAddr ?? '',
      RequestPath: context.RequestPath ?? '/',
      TrackingId: context.trackingID,
      ViewportHeight: context.ViewportHeight ?? window.innerHeight,
      ViewportWidth: context.ViewportWidth ?? window.innerWidth,
    }
  } else if (eventType === 'Add2Cart') {
    eventObject = {
      UniqueId: context?.productId ?? '',
      Price: context.price,
      Quantity: context.quantity,
      Currency: 'USD',
    }
  }

  const eventData: HawkSearchEvent = {
    ClientGuid: clientGuid,
    VisitId: hawkSearchTracking.getVisitId(),
    VisitorId: hawkSearchTracking.getVisitorId(),
    EventType: eventType,
    EventData: btoa(JSON.stringify(eventObject)),
  }

  if (context.vehicle) {
    eventData.CustomDictionary = {
      hawk_distributor_id: StoreInstances.userStore.getUserAttribute(
        UserAttributeKey.partDomain
      ),
      hawk_site_type: HAWK_SITE_TYPE,
      hawk_user_ID: Number(StoreInstances.userStore.preferences.userId),
      vehicle: context.vehicle.engine?.value
        ? `EP-${context.vehicle.make.id}-${context.vehicle.model.id}-${context.vehicle.year.value}-${context.vehicle.engine.id}-${StoreInstances.userStore.country.countryCode}`
        : '',
    }
  }

  return [eventData]
}

export const prepareDataforSalesEvent = (
  eventType: string,
  data: OrderResponse,
  vehicleData?: Vehicle[]
): HawkSearchEvent[] => {
  const requestDataList: HawkSearchEvent[] = []

  for (const orderDetail of data.orderDetails) {
    const vehicleRef = orderDetail.vehicle

    const matchedVehicle = vehicleData?.find(
      (v) =>
        v.engine.value === vehicleRef.engine && v.make.value !== 'MISCELLANEOUS'
    )

    for (const location of orderDetail.locations) {
      for (const order of location.orders) {
        let subtotal = 0
        let total = 0
        const allParts = []

        for (const part of order.parts) {
          const uniqueId =
            part.allianceProductId ?? `${part.lineCode} ${part.partNumber}`
          allParts.push({
            uniqueId,
            ItemPrice: part.unitCostPrice,
            quantity: part.quantityShipped,
          })
          const qty = part.quantityShipped ?? 0
          subtotal += qty * (part.unitCostPrice ?? 0)
          total += qty * ((part.unitCostPrice ?? 0) + (part.unitCorePrice ?? 0))
        }

        if (allParts.length === 0) continue

        const result = {
          OrderNo: order.orderConfirmationNbr,
          Currency: 'USD',
          SubTotal: subtotal,
          Tax: 0,
          Total: total,
          ItemList: allParts,
        }

        const reqData: HawkSearchEvent = {
          ClientGuid: clientGuid,
          VisitId: hawkSearchTracking.getVisitId(),
          VisitorId: hawkSearchTracking.getVisitorId(),
          EventType: eventType,
          EventData: btoa(JSON.stringify(result)),
          CustomDictionary: {
            hawk_distributor_id: StoreInstances.userStore.getUserAttribute(
              UserAttributeKey.partDomain
            ),
            hawk_site_type: HAWK_SITE_TYPE,
            hawk_user_ID: Number(StoreInstances.userStore.preferences.userId),
            vehicle: matchedVehicle
              ? `EP-${matchedVehicle.make.id}-${matchedVehicle.model.id}-${matchedVehicle.year.value}-${matchedVehicle.engine.id}-${StoreInstances.userStore.country.countryCode}`
              : '',
          },
        }

        requestDataList.push(reqData)
      }
    }
  }

  return requestDataList
}
